import {ProductSearchHit} from '../../store/data/productSearchResult/interface';
import {ISwatchImageGroup} from '../../store/data/products/types';
import ProductImage from '../product/Product/ProductImage';
import {IProductTransformationObject, IProductVideo} from '../product/IProduct';
import {buildPlpVideos, extractThronAssetVideos} from '../product/videoUtils';
import {IThronAsset, parseThronAssetsIds} from '../thron/thronUtils';
import ManagerRepository from 'coreClasses/ManagerRepository';
import ProductPromotionModel from 'coreClasses/product/Product/ProductPromotionModel';
import ThronProductImage from 'coreClasses/product/Product/ThronProductImage';
import SearchHitPriceModel from './SearchHitPriceModel';
import VariantModel from 'coreClasses/product/VariantModel';

import {PRODUCT_DISTINCT_SIZES} from 'brandConstants';

/**
 * Represents ProductSearchHitModel
 */
class ProductSearchHitModel {
	private managerRepository: ManagerRepository;
	private productHit: ProductSearchHit;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 * @param {ProductSearchHit} productHit - product search hit
	 */
	constructor(managerRepository: ManagerRepository, productHit: ProductSearchHit) {
		this.managerRepository = managerRepository;
		this.productHit = productHit;
	}

	/**
	 * Returns the product ID.
	 * @return {string} The product ID
	 */
	public getProductId(): string {
		return this.productHit.productId;
	}

	/**
	 * Returns the product URL.
	 * @return {string} The product URL
	 */
	public getUrl(): string {
		return this.managerRepository.getLocalesMgr().updateLinkWithLocale(this.productHit.c_url);
	}

	/**
	 * Returns the product URL.
	 * @return {string} The product URL
	 */
	public getProductUrl(): string {
		return this.getUrl();
	}

	/**
	 * Returns the product main image.
	 * @param {string} viewType - view type
	 * @param {number} index - image index within the group (defaults to 0)
	 * @param {IProductTransformationObject} transformationObject - transformation object
	 * @returns {ProductImage | ThronProductImage} Image object
	 */
	public getProductImage({
		viewType,
		index = 0,
		transformationObject,
	}: {
		viewType?: string;
		index?: number;
		transformationObject: IProductTransformationObject;
	}): ProductImage | ThronProductImage {
		const thronAssets = this.getThronAssets();

		if (thronAssets && thronAssets.length > 0) {
			const thronImages = thronAssets.filter((a) => a.type === 'image');

			if (thronImages && thronImages.length > 0) {
				const image = thronImages[index] || thronImages[0];

				return new ThronProductImage(
					{
						contentId: image.id,
						productId: this.getProductId(),
						alt: this.getName(),
					},
					transformationObject,
				);
			}
		}

		// TODO to be fixed, not every brand use it correctly
		const correctViewType = viewType || transformationObject?.viewType;

		if (this.productHit?.imageGroups?.length > 0 && correctViewType) {
			const imageGroup = this.productHit.imageGroups.find(
				(imageGroup) =>
					imageGroup.viewType === correctViewType &&
					// hit doesn't have color information to chose the right image so product id is needed
					this.productHit.productId.includes(imageGroup?.variationAttributes?.[0]?.values?.[0]?.value),
			);

			if (imageGroup) {
				const targetImage = imageGroup.images[index] || imageGroup.images[0];

				return new ProductImage(targetImage, transformationObject, this.getProductId());
			}
		}

		return new ProductImage(this.productHit.image, transformationObject, this.getProductId());
	}

	/**
	 * Returns the product main image.
	 * @param {String} viewType - view type
	 * @param {IProductTransformationObject} transformationObject - transformation object
	 * @returns {ProductImage[] | ThronProductImage[]} Image object
	 */
	public getProductImages(
		viewType: String,
		transformationObject: IProductTransformationObject,
	): ProductImage[] | ThronProductImage[] {
		const thronAssets = this.getThronAssets();

		if (thronAssets.length > 0) {
			return thronAssets
				.filter((a) => a.type === 'image')
				.map((image) => {
					return new ThronProductImage(
						{
							contentId: image.id,
							productId: this.getProductId(),
							alt: this.getName(),
						},
						transformationObject,
					);
				});
		}

		const correctViewType = viewType || transformationObject?.viewType;

		if (this.productHit?.imageGroups?.length > 0 && correctViewType) {
			const imageGroup = this.productHit.imageGroups.find(
				(imageGroup) =>
					imageGroup.viewType === correctViewType &&
					this.productHit.productId.includes(imageGroup?.variationAttributes?.[0]?.values?.[0]?.value),
			);

			if (imageGroup && imageGroup.images.length > 0) {
				return imageGroup.images.map((img) => new ProductImage(img, transformationObject, this.getProductId()));
			}
		}

		return [this.getProductImage({transformationObject})] as ProductImage[] | ThronProductImage[];
	}

	/**
	 * Returns the product videos extracted from Thron assets.
	 * @returns {Array.<IProductVideo>} Array of videos
	 */
	public getVideos(): IProductVideo[] {
		const thronAssets = this.getThronAssets();

		if (thronAssets.length > 0) {
			return extractThronAssetVideos(thronAssets);
		}

		return [];
	}

	/**
	 * Returns videos to display on the Product Listing Page (PLP).
	 * Merges videos from the c_plpProductVideos attribute (Thron IDs, optional positions)
	 * with videos from Thron assets, avoiding duplicates and resolving position conflicts.
	 * @returns {Array.<IProductVideo>} Array of videos sorted by position
	 */
	public getPlpVideos(): IProductVideo[] {
		return buildPlpVideos(this.productHit?.representedProduct?.c_plpProductVideos, this.getThronAssets());
	}

	/**
	 * Parses c_thronAssetsIds attribute (set-of-string).
	 * Each entry has format "contentId:type" where type is "image" or "video".
	 * @returns {Array.<IThronAsset>} Array of Thron assets
	 */
	public getThronAssets(): IThronAsset[] {
		return parseThronAssetsIds(this.productHit?.representedProduct?.c_thronAssetsIds);
	}

	/**
	 * Returns the product name.
	 * @return {string} The product Name
	 */
	public getName(): string {
		return this.productHit.productName;
	}

	/**
	 * Returns the product brand.
	 * @return {string} The product brand
	 */
	public getBrand() {
		return this.productHit.c_brand;
	}

	/**
	 * Returns the short description of the product
	 * (read from c_shortDescription populated by the OCAPI productSearch hook).
	 * @return {string} The product short description
	 */
	public getShortDescription(): string {
		return this.productHit?.c_shortDescription || '';
	}

	/**
	 * Returns the product name.
	 * @return {number} The product initial price
	 */
	public getPrice(): number {
		return {
			value: this.productHit?.price,
			currency: this.productHit?.currency,
			pricebook: 'default',
		};
	}

	/**
	 * Returns the product price currency
	 * @returns {string} product price currency
	 */
	public getPriceCurrency(): string {
		return this.productHit?.currency || '';
	}

	/**
	 * Returns the price model
	 * @returns {SearchHitPriceModel} price model
	 */
	public getPriceModel(): SearchHitPriceModel {
		return new SearchHitPriceModel(this);
	}

	/**
	 * Retrieves the index of the alternative image for the product.
	 * @returns {number} The index of the alternative image, if available.
	 */
	getAlternativeImageIndex(): number {
		return this.productHit?.representedProduct?.c_alternativeImageIndex;
	}

	/**
	 * Returns category IDs for which the product should display the model_alt image.
	 * @returns {string} Category IDs string
	 */
	getCategoryIdForAlternativeImage(): string {
		return this.productHit?.representedProduct?.c_categoryIdForAlternativeImage || '';
	}

	/**
	 * Returns the variation groups.
	 * @returns {ProductSearchHit['c_variationGroups']} The variation groups
	 */
	getVariationGroups(): ProductSearchHit['c_variationGroups'] {
		return this.productHit?.c_variationGroups || this.getDefaultVariationGroups();
	}

	/**
	 * Returns the primary category id
	 * @returns {String | null} primary category id
	 */
	getPrimaryCategoryId(): string | null {
		return this.productHit?.c_primaryCategory || null;
	}

	/**
	 * Returns the product color description
	 * @returns {String | null} product color description
	 */
	getColorDescription(): string | null {
		return this.productHit?.representedProduct?.c_colorDescription || null;
	}

	/**
	 * Returns the product size
	 * @returns {String | null} product size
	 */
	getSize(): string | null {
		return this.productHit?.representedProduct?.c_size || null;
	}

	/**
	 * Returns the product badges
	 * @returns {String[]} product badges IDs
	 */
	getBadgesIds(): string[] {
		return this.productHit?.representedProduct?.c_badges?.split(',') || [];
	}

	/**
	 * Returns the product image badges
	 * @returns {String[]} product image badges IDs
	 */
	getImageBadgesIds(): string[] {
		return this.productHit?.representedProduct?.c_imageBadge?.split(',') || [];
	}

	/**
	 * Returns the out of stock status for the product.
	 * @returns {Boolean} True if the product is out of stock
	 */
	isOutOfStock(): boolean {
		return this.productHit?.orderable === false;
	}

	/**
	 * Returns the promotions of the product
	 * @returns {ProductPromotionModel[]} Product Promotion
	 */
	getProductPromotions(): ProductPromotionModel[] {
		return (
			this.productHit?.productPromotions?.map(
				(productPromotion) => new ProductPromotionModel(productPromotion),
			) || []
		);
	}

	/**
	 * Returns all variation group ids for product with multiple variation groups
	 * @returns {Array<string>}
	 */
	getVariationGroupIds(): Array<string> {
		return this.getVariationGroups().map((vg) => vg.productId);
	}

	/**
	 * Returns the list of valid hex colors from the hexadecBasicColor product attribute
	 * @return {string[]}
	 */
	getProductHexColors(): string[] {
		const hexColors = this.productHit?.representedProduct?.c_hexadecBasicColor ?? [];
		const hexReg = /^#([0-9a-f]{3}){1,2}$/i;
		const transparencyHexReg = /^#[0-9A-F]{6}[0-9a-f]{0,2}$/i;

		return hexColors.filter((hexColor) => {
			const color = hexColor.trim();

			return hexReg.test(color) || transparencyHexReg.test(color);
		});
	}

	/**
	 * Returns string with hexadecBasicColor product attribute as a value
	 * @return {string}
	 */
	getProductHexColor(): string {
		const hexColors = this.getProductHexColors();

		if (hexColors.length === 0) {
			return '';
		}

		return hexColors[0];
	}

	/**
	 * Returns object with variation groups ID as a key and first hexadecBasicColor product attribute as a value
	 * @return {Record<string, string>}
	 */
	getVariationGroupsHexColor(): Record<string, string> {
		return this.productHit?.c_variationGroupsHexColor || {};
	}

	/**
	 * Returns object with variation groups ID as a key and colorDescription product attribute as a value
	 * @return {Record<string, string>}
	 */
	getVariationGroupsColorDescription(): Record<string, string> {
		return this.productHit?.c_variationGroupsColorDescription || {};
	}

	/**
	 * Returns the swatch image groups indexed by variation group id
	 * @return {Record<string, Omit<ISwatchImageGroup, 'productUrl'>>} variation groups swatch image groups
	 */
	getSwatchImageGroups(): Record<string, Omit<ISwatchImageGroup, 'productUrl'>> {
		const swatchImageGroups = this.productHit?.c_swatchImageGroups || {};
		const normalizedGroups: Record<string, Omit<ISwatchImageGroup, 'productUrl'>> = {};

		Object.entries(swatchImageGroups).forEach(([variationGroupId, group]) => {
			normalizedGroups[variationGroupId] = {
				imageUrl: group.imageUrl,
				alt: group.alt,
				title: group.title,
			};
		});

		return normalizedGroups;
	}

	/**
	 * Returns the category id
	 * @returns {String} primary category id
	 */
	getCategoryId(): string {
		return this.productHit?.representedProduct?.c_categoryId;
	}

	/**
	 * Returns the product season
	 * @returns {string} season
	 */
	getSeason(): string {
		return this.productHit?.representedProduct?.c_season;
	}

	/**
	 * Returns the product material description
	 * @returns {String} product material description
	 */
	getMaterialDescription(): string {
		return this.productHit?.representedProduct?.c_materialDescription || '';
	}

	/**
	 * Always ready
	 */
	isReady(): boolean {
		return true;
	}

	/**
	 * Always true
	 */
	isVariationGroup(): boolean {
		return true;
	}

	/**
	 * We only query VGs
	 */
	getVariationGroupId(): string {
		return this.getProductId();
	}

	/**
	 * Returns Brand Category Id
	 * @returns {String} product brand category id
	 */
	getBrandCategoryId(): string {
		return this.productHit?.representedProduct?.c_brandCategoryId;
	}

	/**
	 * Returns the variation groups
	 */
	getDefaultVariationGroups() {
		return this.productHit?.variationGroups || [];
	}

	/**
	 * Returns variation values
	 */
	getVariationValues() {
		return this.getDefaultVariationGroups().find((vg) => vg.productId === this.getProductId())?.variationValues;
	}

	/**
	 * Returns a string with the variation attribute value
	 * @param {String} variationAttributeName the name of the variation attribute
	 * @returns {String} variation attribute name
	 */
	getVariationValue(variationAttributeName: string): string {
		return this.productHit?.variationGroups
			?.find((v) => v.productId === this.getProductId())?.variationValues?.[variationAttributeName] || '';
	}

	/**
	 * Returns the product update timestamp - placeHolder for compatibility with ProductModel
	 * @returns {undefined}
	 */
	getUpdateTimestamp(): undefined {
		return undefined;
	}

	/**
	 * Returns the product variants
	 * @param {boolean} distinctSizes - returns only one variation per variation value size
	 * @param {string | null} length - length of the product
	 * @returns {VariantModel[]} product variants
	 */
	getVariants(distinctSizes: boolean = PRODUCT_DISTINCT_SIZES, length: string = null): VariantModel[] {
		const searchRefinementSizeSorting = this.managerRepository
			.getConfigurationsMgr()
			.get('organizationPreferences.SearchRefinementSizeSorting');

		let variants =
			this.productHit?.variants?.map(
				(variant) =>
					new VariantModel(this.managerRepository, {
						...variant,
						id: variant.productId,
						currency: this.productHit.currency,
						variationGroups: this.productHit.variationGroups,
						c_variationGroupsColorGroup: this.productHit.c_variationGroupsColorGroup,
						c_productType: this.productHit.c_productType,
						c_variationGroupsHexColor: this.productHit.c_variationGroupsHexColor,
						c_showStockThreshold: this.productHit.c_showStockThreshold,
						c_hasAvailableVariant: this.productHit.c_hasAvailableVariant,
						c_isAlcohol: this.productHit.c_isAlcohol,
						c_isFood: this.productHit.c_isFood,
						c_size: variant?.variationValues?.size,
						c_color: variant?.variationValues?.color,
						slugUrl: this.productHit.c_url
					}),
			) || [];

		if (searchRefinementSizeSorting && variants) {
			const sortingPattern = searchRefinementSizeSorting.split(';');

			variants.sort((obj1, obj2) => {
				const size1 = obj1.getSize();
				const size2 = obj2.getSize();
				let toCompare1 = sortingPattern.indexOf(size1);
				let toCompare2 = sortingPattern.indexOf(size2);

				if (toCompare1 === -1 || toCompare2 === -1) {
					// Fallback on sizes natural order
					toCompare1 = size1;
					toCompare2 = size2;
				}

				return toCompare1 > toCompare2 ? 1 : toCompare1 < toCompare2 ? -1 : 0;
			});
		}

		if (distinctSizes) {
			variants = variants.reduce((newArray, variant, index) => {
				if (length && variant.getWidth() !== length) {
					return newArray;
				}

				const variationValue = variant.getVariationValue('size');
				const alreadyAdded = newArray.find((v) => v.getVariationValue('size') === variationValue);

				if (alreadyAdded) {
					return newArray;
				} else if (variant.getAtsStockLevel() > 0) {
					return [...newArray, variant];
				} else {
					const inStockAlternative = variants.find(
						(v, i) =>
							i !== index && v.getVariationValue('size') === variationValue && v.getAtsStockLevel() > 0,
					);

					return [...newArray, inStockAlternative ? inStockAlternative : variant];
				}
			}, []);
		}

		return variants;
	}
}

/**
 * Guard for check type of ProductSearchHitModel
 */
export const isProductSearchHitModel = (data: any): data is ProductSearchHitModel =>
	data instanceof ProductSearchHitModel;

export default ProductSearchHitModel;
